import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, ImagePlus } from 'lucide-react';
import FileUpload from '@/components/FileUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const categories = ['Wedding', 'Anniversary', 'Dinner Party', 'Engagement', 'General Party', 'Other'];
const MAX_IMAGES = 20;

const AdminPortfolio = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', category: 'Wedding', images: [], description: '' });

  useEffect(() => { fetchPortfolio(); }, []);

  const fetchPortfolio = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`);
      setPortfolio(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getImages = (item) => {
    if (item.images && item.images.length > 0) return item.images;
    if (item.image_url) return [item.image_url];
    return [];
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        category: item.category,
        images: getImages(item),
        description: item.description || '',
      });
    } else {
      setEditingItem(null);
      setFormData({ title: '', category: 'Wedding', images: [], description: '' });
    }
    setIsDialogOpen(true);
  };

  const addImage = (url) => {
    if (!url) return;
    if (formData.images.length >= MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    setFormData(p => ({ ...p, images: [...p.images, url] }));
  };

  const removeImage = (index) => {
    setFormData(p => ({ ...p, images: p.images.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!formData.title || formData.images.length === 0) {
      toast.error('Title and at least one image are required');
      return;
    }
    setIsSaving(true);
    const token = getAuthToken();
    const payload = {
      title: formData.title,
      category: formData.category,
      image_url: formData.images[0],
      images: formData.images,
      description: formData.description,
    };
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/portfolio/${editingItem.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/portfolio`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Saved!');
      setIsDialogOpen(false);
      fetchPortfolio();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    const token = getAuthToken();
    try {
      await axios.delete(`${API}/admin/portfolio/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted!');
      fetchPortfolio();
    } catch (error) {
      toast.error('Failed');
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-portfolio">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Portfolio</h1>
        <Button onClick={() => openDialog()} className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full">
          <Plus size={18} className="mr-2" /> Add Item
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {portfolio.map((item) => {
          const imgs = getImages(item);
          return (
            <div key={item.id} className="bg-white rounded-2xl shadow-soft overflow-hidden">
              <div className="h-48 overflow-hidden relative">
                <img src={imgs[0] || ''} alt={item.title} className="w-full h-full object-cover" />
                {imgs.length > 1 && (
                  <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                    {imgs.length} photos
                  </div>
                )}
              </div>
              <div className="p-6">
                <span className="text-xs text-[#D4AF37] uppercase tracking-wider">{item.category}</span>
                <h3 className="font-heading text-xl text-[#2C2C2C] mb-4">{item.title}</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openDialog(item)} className="border-[#E8DFD0]"><Pencil size={14} className="mr-1" /> Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 border-red-200"><Trash2 size={14} className="mr-1" /> Delete</Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editingItem ? 'Edit' : 'Add'} Portfolio Item</DialogTitle></DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} className="input-elegant" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                <SelectTrigger className="input-elegant"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Multi-image upload */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Photos ({formData.images.length}/{MAX_IMAGES}) *
              </Label>

              {/* Image grid */}
              {formData.images.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {formData.images.map((url, i) => (
                    <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-[#E8DFD0]">
                      <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                      {i === 0 && (
                        <div className="absolute top-1 left-1 bg-[#D4AF37] text-white text-[10px] px-1.5 py-0.5 rounded">Cover</div>
                      )}
                      <button
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button */}
              {formData.images.length < MAX_IMAGES && (
                <FileUpload
                  onUpload={addImage}
                  accept="image/*"
                  label={formData.images.length === 0 ? 'Upload first photo (this will be the cover)' : `Add more photos (${MAX_IMAGES - formData.images.length} remaining)`}
                />
              )}
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="input-elegant" />
            </div>
            <div className="flex gap-4 pt-4">
              <Button onClick={() => setIsDialogOpen(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={isSaving} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-white">{isSaving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPortfolio;
